import { useEffect, useState } from 'react';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { 
  Utensils, 
  Image as ImageIcon, 
  Clock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  History
} from 'lucide-react';

export default function DashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    menuItems: 0,
    categories: 0,
    galleryImages: 0,
    activityLogs: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentLogs, setRecentLogs] = useState<any[]>([]);

  useEffect(() => {
    // Real-time stats
    const unsubMenu = onSnapshot(collection(db, 'menu_items'), (snap) => {
      setStats(prev => ({ ...prev, menuItems: snap.size }));
    });
    const unsubCat = onSnapshot(collection(db, 'categories'), (snap) => {
      setStats(prev => ({ ...prev, categories: snap.size }));
    });
    const unsubGallery = onSnapshot(collection(db, 'gallery'), (snap) => {
      setStats(prev => ({ ...prev, galleryImages: snap.size }));
    });
    const unsubLogs = onSnapshot(collection(db, 'activity_logs'), (snap) => {
      setStats(prev => ({ ...prev, activityLogs: snap.size }));
    });

    // Recent Activity
    const q = query(collection(db, 'activity_logs'), orderBy('timestamp', 'desc'), limit(5));
    const unsubRecent = onSnapshot(q, (snap) => {
      setRecentLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => {
      unsubMenu();
      unsubCat();
      unsubGallery();
      unsubLogs();
      unsubRecent();
    };
  }, []);

  const cards = [
    { label: t('admin_stat_menu_items'), value: stats.menuItems.toString(), icon: Utensils, color: 'text-[#F57F25]' },
    { label: t('admin_stat_categories'), value: stats.categories.toString(), icon: Clock, color: 'text-blue-500' },
    { label: t('admin_stat_gallery_photos'), value: stats.galleryImages.toString(), icon: ImageIcon, color: 'text-purple-500' },
    { label: t('admin_stat_logs_recorded'), value: stats.activityLogs.toString(), icon: History, color: 'text-green-500' },
  ];

  if (loading && stats.menuItems === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-2">{t('admin_dashboard_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_dashboard_sub')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card) => (
          <div key={card.label} className="bg-[#111] border border-white/5 p-8 rounded-[32px] space-y-4 hover:border-white/10 transition-colors group">
            <div className={`p-4 bg-white/5 inline-block rounded-2xl ${card.color} group-hover:scale-110 transition-transform`}>
              <card.icon size={28} />
            </div>
            <div>
              <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">{card.label}</p>
              <p className="text-4xl font-black text-white mt-1 tracking-tight">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <div className="bg-[#111] border border-white/5 p-8 rounded-[40px]">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-white font-black text-xl uppercase tracking-tight">{t('admin_recent_activity')}</h3>
            <History size={20} className="text-white/20" />
          </div>
          
          <div className="space-y-6">
            {recentLogs.length === 0 ? (
              <p className="text-white/20 text-sm font-bold uppercase tracking-widest text-center py-10">{t('admin_no_recent_activity')}</p>
            ) : (
              recentLogs.map((log) => (
                <div key={log.id} className="flex gap-4 items-start pb-6 border-b border-white/5 last:border-0 last:pb-0">
                  <div className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center text-white/30 shrink-0">
                    <CheckCircle2 size={20} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">
                      {log.userName} <span className="text-[#F57F25] text-xs uppercase ml-1">[{log.action}]</span>
                    </p>
                    <p className="text-white/40 text-xs mt-1">
                      {log.module}: {log.targetName}
                    </p>
                    <p className="text-white/10 text-[9px] uppercase font-black tracking-widest mt-2">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-[#F57F25] p-10 rounded-[40px] flex flex-col items-center justify-center text-center space-y-6">
          <div className="w-20 h-20 bg-black/20 rounded-full flex items-center justify-center text-black">
            <AlertCircle size={40} />
          </div>
          <div>
            <h3 className="text-black font-black text-2xl uppercase tracking-tight italic">{t('admin_system_status')}</h3>
            <p className="text-black/60 text-sm mt-2 font-bold max-w-xs">{t('admin_system_status_ok')}</p>
          </div>
          <div className="flex items-center gap-2 bg-black/10 px-4 py-2 rounded-full">
            <div className="w-2 h-2 bg-green-900 rounded-full animate-pulse"></div>
            <span className="text-black font-black text-[10px] uppercase tracking-widest">{t('admin_live_connection')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
